import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Card } from '@swyftx/aviary/atoms/Card';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Heading } from '@swyftx/aviary/atoms/Typography';
import { useTailwindBreakpoint } from '@swyftx/aviary/hooks/useTailwindBreakpoint';
import { Loading } from '@swyftx/aviary/icons/outlined';
import { MarginPositionStatus } from '@swyftx/margin/build/types';

import { useMarginPositions } from 'src/lib/margin/hooks/positions';
import { MarginOrdersType } from 'src/lib/margin/types';
import { useNavigateRoute } from 'src/lib/navigation/hooks';
import { NavigationURLs } from 'src/lib/navigation/types';

import { MarginPositionsHealthTable } from './MarginPositionsHealthTable';
import { MarginPositionsTableEmpty } from '../../General';

type Props = {
  className?: string;
};

const MarginPositionsHealth: React.FC<Props> = ({ className }) => {
  const { positions, isLoading, isError } = useMarginPositions({ status: [MarginPositionStatus.Open] });
  const { navigate } = useNavigateRoute();
  const { t } = useTranslation('margin');
  const isXs = useTailwindBreakpoint('xs');

  return (
    <FlexLayout direction='column' spacing={12} className={className}>
      <Card className='h-full w-full overflow-hidden pb-12 @container' data-container='position-health-table'>
        <FlexLayout
          direction='column'
          alignItems='center'
          justifyContent='center'
          className='h-full w-full'
          spacing={12}
        >
          {(isLoading || !!positions?.length) && (
            <FlexLayout spacing={4} direction='column' className='w-full px-16 pt-16 md:px-24 md:pt-24'>
              <Heading size={isXs ? 'h6' : 'h5'}>{t('positionHealth.title')}</Heading>
              <Body size='xsmall' color='secondary' className='hidden sm:block'>
                {t('positionHealth.subTitle')}
              </Body>
            </FlexLayout>
          )}
          {positions?.length ? (
            <FlexLayout spacing={24} direction='column' className='h-full w-full'>
              <MarginPositionsHealthTable className='px-16 md:px-24' />
            </FlexLayout>
          ) : (
            <>
              {isLoading ? (
                <FlexLayout className='h-full w-full p-24' justifyContent='center' alignItems='center'>
                  <Loading className='animate-spin text-color-text-primary' />
                </FlexLayout>
              ) : (
                <MarginPositionsTableEmpty
                  ordersType={MarginOrdersType.Position}
                  isError={isError}
                  action={
                    <Button
                      variant='filled'
                      size='lg'
                      color='accent'
                      onClick={() => navigate(NavigationURLs.MarginTrade)}
                    >
                      {t('positionHealth.buttons.openPosition')}
                    </Button>
                  }
                />
              )}
            </>
          )}
        </FlexLayout>
      </Card>
    </FlexLayout>
  );
};

export { MarginPositionsHealth };
